import { cn } from "@omni/utils/styles/tw.merge";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { HTMLInputAutoCompleteAttribute, useCallback, useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import { Path, PathValue, useFormContext } from "react-hook-form";

import { PrefixedInputField } from "@/components/Form/RHF/FormFields/PrefixedInput/prefixedInput.component";
import { PhonenumberPrefix } from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.types";

import { BaseFieldProps } from "../BaseField/BaseField.component";
import { FormFieldProps, isFormRequired } from "../formField.types";
import { BaseOption, LabelType } from "../SelectField/selectField.component";
import { PhoneFieldValues } from "./phoneField.schema";

type PhoneFieldProperties = {
    placeholder: string;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    phonenumberPrefixes: Array<PhonenumberPrefix>;
};

export type PhoneFieldProps<T extends PhoneFieldValues> = FormFieldProps<
    Omit<BaseFieldProps<PhoneFieldValues, "phone">, "name" | "required"> &
        PhoneFieldProperties & {
            required: isFormRequired<T, Extract<Path<T>, "phone">>;
        },
    T
>;

type PhonePrefixOptions = BaseOption<
    PathValue<PhoneFieldValues, Path<PhoneFieldValues>>
> & {
    isoCode?: string;
};

const PhoneField = <T extends PhoneFieldValues = never>({
    label,
    placeholder,
    autoComplete,
    required,
    disabled,
    phonenumberPrefixes
}: PhoneFieldProps<T>) => {
    const { subscribe, setValue } = useFormContext<PhoneFieldValues>();

    const getPrefix = useCallback(
        (prefix: string) => {
            return phonenumberPrefixes.find((p) => p.prefix === prefix)?.country
                .isoCode;
        },
        [phonenumberPrefixes]
    );

    useEffect(() => {
        const callback = subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                const phoneNumberValue = values.phone;
                const phoneNumberPrefixValue = values.phonePrefix;

                let phone;
                let isValid = false;

                if (phoneNumberValue) {
                    try {
                        phone = parsePhoneNumberWithError(
                            phoneNumberValue || "",
                            getPrefix(phoneNumberPrefixValue || "")
                        );
                        isValid = phone.isValid();
                    } catch (_) {
                        isValid = false;
                    }
                }

                if (!isValid || !phone) {
                    return;
                }

                if (phoneNumberPrefixValue !== `+${phone.countryCallingCode}`) {
                    setValue("phonePrefix", `+${phone.countryCallingCode}`);
                }
                if (phoneNumberValue !== phone.nationalNumber) {
                    setValue("phone", phone.nationalNumber);
                }
            }
        });

        return () => callback();
    }, [subscribe, setValue, getPrefix]);

    if (!phonenumberPrefixes?.length) {
        return null;
    }

    const options: Array<PhonePrefixOptions> = phonenumberPrefixes.map(
        (item) => {
            return {
                isoCode: item.country.isoCode,
                label: item.country.name,
                value: item.prefix
            };
        }
    );

    if (options.length > 0) {
        options.splice(5, 0, {
            label: LabelType.DIVIDER,
            isDisabled: true
        });
    }

    const renderOption = (option: PhonePrefixOptions) => {
        if (option.label === LabelType.DIVIDER) {
            return <hr />;
        }

        if (!option.isoCode) {
            return null;
        }

        return (
            <div className={cn("tw:text-nowrap tw:flex tw:items-center")}>
                <ReactCountryFlag
                    svg
                    countryCode={option.isoCode}
                    className={cn("tw:h-6! tw:w-6!")}
                />
                <span className={cn("tw:pl-2")}>{option.value}</span>
            </div>
        );
    };

    return (
        <PrefixedInputField<PhoneFieldValues, PhonePrefixOptions>
            name={"phone"}
            prefixName={"phonePrefix"}
            label={label}
            prefixOptions={options}
            required={required}
            renderOption={renderOption}
            placeholder={placeholder}
            autoComplete={autoComplete}
            type="tel"
            disabled={disabled}
        />
    );
};

export { PhoneField };
